import { lazy, Suspense } from "react";
import { PageLoading } from "../components/PageLoading.js";

import { createBrowserRouter, Navigate, type RouteObject } from "react-router-dom";

import { ChangePasswordPage } from "../pages/ChangePasswordPage.js";
import { LoginPage } from "../pages/LoginPage.js";

const AppShell = lazy(() =>
  import("./app-shell.js").then((module) => ({ default: module.AppShell })),
);
import { LegacyRedirect } from "./legacy-redirect.js";
import { RouteErrorPage } from "./route-error.js";
import { LEGACY_REDIRECTS, PUBLIC_LEGACY_REDIRECTS, ROUTE_PATTERNS, ROUTES } from "./routes.js";
import { SessionGate } from "./session-gate.js";

const FeatureReadinessPage = lazy(() =>
  import("../pages/FeatureReadinessPage.js").then((module) => ({
    default: module.FeatureReadinessPage,
  })),
);
const IntegrationControlPage = lazy(() =>
  import("../pages/IntegrationControlPage.js").then((module) => ({
    default: module.IntegrationControlPage,
  })),
);
const IntegrationProductPage = lazy(() =>
  import("../pages/IntegrationProductPage.js").then((module) => ({
    default: module.IntegrationProductPage,
  })),
);
const AppHomePage = lazy(() =>
  import("../pages/AppHomePage.js").then((module) => ({ default: module.AppHomePage })),
);
const AttentionPage = lazy(() =>
  import("../pages/AttentionPage.js").then((module) => ({ default: module.AttentionPage })),
);
const AudiencePage = lazy(() =>
  import("../pages/AudiencePage.js").then((module) => ({ default: module.AudiencePage })),
);
const DependencyPage = lazy(() =>
  import("../pages/DependencyPage.js").then((module) => ({ default: module.DependencyPage })),
);
const FeedbackDetailPage = lazy(() =>
  import("../pages/FeedbackDetailPage.js").then((module) => ({
    default: module.FeedbackDetailPage,
  })),
);
const FeedbackPage = lazy(() =>
  import("../pages/FeedbackPage.js").then((module) => ({ default: module.FeedbackPage })),
);
const SupportInboxPage = lazy(() =>
  import("../pages/SupportInboxPage.js").then((module) => ({ default: module.SupportInboxPage })),
);
const SupportTicketPage = lazy(() =>
  import("../pages/SupportTicketPage.js").then((module) => ({ default: module.SupportTicketPage })),
);
const AiPage = lazy(() =>
  import("../pages/AiPage.js").then((module) => ({ default: module.AiPage })),
);
const IntegrationsPage = lazy(() =>
  import("../pages/IntegrationsPage.js").then((module) => ({ default: module.IntegrationsPage })),
);
const TingeeLabPage = lazy(() =>
  import("../pages/TingeeLabPage.js").then((module) => ({ default: module.TingeeLabPage })),
);
const BankHubLabPage = lazy(() =>
  import("../pages/BankHubLabPage.js").then((module) => ({ default: module.BankHubLabPage })),
);
const EinvoiceAgencyPage = lazy(() =>
  import("../pages/EinvoiceAgencyPage.js").then((module) => ({
    default: module.EinvoiceAgencyPage,
  })),
);
const EinvoiceLabPage = lazy(() =>
  import("../pages/EinvoiceLabPage.js").then((module) => ({ default: module.EinvoiceLabPage })),
);
const LabFixturesPage = lazy(() =>
  import("../pages/LabFixturesPage.js").then((module) => ({ default: module.LabFixturesPage })),
);
const LabIndexPage = lazy(() =>
  import("../pages/LabIndexPage.js").then((module) => ({ default: module.LabIndexPage })),
);
const LabRunsPage = lazy(() =>
  import("../pages/LabRunsPage.js").then((module) => ({ default: module.LabRunsPage })),
);
const CredentialSetHistoryPage = lazy(() =>
  import("../pages/CredentialSetHistoryPage.js").then((module) => ({
    default: module.CredentialSetHistoryPage,
  })),
);
const OverviewPage = lazy(() =>
  import("../pages/OverviewPage.js").then((module) => ({ default: module.OverviewPage })),
);
const PlatformPage = lazy(() =>
  import("../pages/PlatformPage.js").then((module) => ({ default: module.PlatformPage })),
);
const OwnershipPage = lazy(() =>
  import("../pages/OwnershipPage.js").then((module) => ({ default: module.OwnershipPage })),
);
const ProductDetailPage = lazy(() =>
  import("../pages/ProductsPage.js").then((module) => ({ default: module.ProductDetailPage })),
);
const ProductsPage = lazy(() =>
  import("../pages/ProductsPage.js").then((module) => ({ default: module.ProductsPage })),
);
const PlanMappingPage = lazy(() =>
  import("../pages/PlanMappingPage.js").then((module) => ({ default: module.PlanMappingPage })),
);
const BankTransferPage = lazy(() =>
  import("../pages/BankTransferPage.js").then((module) => ({ default: module.BankTransferPage })),
);
const SharedHubPage = lazy(() =>
  import("../pages/SharedHubPage.js").then((module) => ({ default: module.SharedHubPage })),
);
const AppLinksPage = lazy(() =>
  import("../pages/AppLinksPage.js").then((module) => ({ default: module.AppLinksPage })),
);
const PartnerReportPage = lazy(() =>
  import("../pages/PartnerReportPage.js").then((module) => ({ default: module.PartnerReportPage })),
);
const ShopIntegrationCardsPage = lazy(() =>
  import("../pages/ShopIntegrationCardsPage.js").then((module) => ({
    default: module.ShopIntegrationCardsPage,
  })),
);
const CustomerUsersPage = lazy(() =>
  import("../pages/CustomerUsersPage.js").then((module) => ({ default: module.CustomerUsersPage })),
);
const AcquisitionOpsPage = lazy(() =>
  import("../pages/AcquisitionOpsPage.js").then((module) => ({ default: module.AcquisitionOpsPage })),
);
const ResalePage = lazy(() =>
  import("../pages/ResalePage.js").then((module) => ({ default: module.ResalePage })),
);
const InvoiceQuotaPage = lazy(() =>
  import("../pages/InvoiceQuotaPage.js").then((module) => ({ default: module.InvoiceQuotaPage })),
);
const InvoiceQuotaWorkspacePage = lazy(() =>
  import("../pages/InvoiceQuotaWorkspacePage.js").then((module) => ({ default: module.InvoiceQuotaWorkspacePage })),
);
const CustomerWorkspacePage = lazy(() =>
  import("../pages/CustomerWorkspacePage.js").then((module) => ({
    default: module.CustomerWorkspacePage,
  })),
);
const CustomerWorkspacesPage = lazy(() =>
  import("../pages/CustomerWorkspacesPage.js").then((module) => ({
    default: module.CustomerWorkspacesPage,
  })),
);
const RevenuePage = lazy(() =>
  import("../pages/RevenuePage.js").then((module) => ({ default: module.RevenuePage })),
);
const SubscriptionsPage = lazy(() =>
  import("../pages/SubscriptionsPage.js").then((module) => ({ default: module.SubscriptionsPage })),
);
const CostPage = lazy(() => import("../pages/CostPage.js").then((module) => ({ default: module.CostPage })));
const ReliabilityPage = lazy(() =>
  import("../pages/ReliabilityPage.js").then((module) => ({ default: module.ReliabilityPage })),
);
const MailAppPage = lazy(() =>
  import("../pages/MailAppPage.js").then((module) => ({ default: module.MailAppPage })),
);
const MailProviderPage = lazy(() =>
  import("../pages/MailProviderPage.js").then((module) => ({ default: module.MailProviderPage })),
);
const MailProvidersPage = lazy(() =>
  import("../pages/MailProvidersPage.js").then((module) => ({ default: module.MailProvidersPage })),
);
const MailStatusPage = lazy(() =>
  import("../pages/MailStatusPage.js").then((module) => ({ default: module.MailStatusPage })),
);
const RunDetailPage = lazy(() =>
  import("../pages/RunDetailPage.js").then((module) => ({ default: module.RunDetailPage })),
);
const ServiceDetailPage = lazy(() =>
  import("../pages/ServiceDetailPage.js").then((module) => ({ default: module.ServiceDetailPage })),
);
const SourceDetailPage = lazy(() =>
  import("../pages/SourceDetailPage.js").then((module) => ({ default: module.SourceDetailPage })),
);
const SourcesPage = lazy(() =>
  import("../pages/SourcesPage.js").then((module) => ({ default: module.SourcesPage })),
);
const StaffPage = lazy(() =>
  import("../pages/StaffPage.js").then((module) => ({ default: module.StaffPage })),
);
const TeamsPage = lazy(() =>
  import("../pages/TeamsPage.js").then((module) => ({ default: module.TeamsPage })),
);

/**
 * Cây route — đường dẫn khai ở `routes.ts`, nhóm khớp bốn nhóm của sidebar.
 *
 * `/attention` là trang mở đầu, không phải `/overview`: người điều hành mở hub để biết việc cần
 * làm, không phải để đọc ba mươi thẻ số.
 */
const ROUTE_TREE: RouteObject[] = [
  { path: ROUTES.login, element: <LoginPage /> },
  ...PUBLIC_LEGACY_REDIRECTS.map((r) => ({ path: r.from, element: <LegacyRedirect to={r.to} /> })),
  {
    path: ROUTES.changePassword,
    element: (
      <SessionGate>
        <ChangePasswordPage />
      </SessionGate>
    ),
  },
  {
    path: "/",
    element: (
      <SessionGate>
        <Suspense fallback={<PageLoading />}>
          <AppShell />
        </Suspense>
      </SessionGate>
    ),
    children: [
      { index: true, element: <Navigate to={ROUTES.attention} replace /> },

      // ── Chung ──
      { path: ROUTES.attention, element: <AttentionPage /> },
      { path: ROUTES.overview, element: <OverviewPage /> },
      { path: ROUTES.integrations, element: <IntegrationsPage /> },
      { path: ROUTES.integrationAccounts, element: <IntegrationControlPage mode="accounts" /> },
      {
        path: ROUTES.integrationConnections,
        element: <IntegrationControlPage mode="connections" />,
      },
      { path: ROUTES.integrationBindings, element: <IntegrationControlPage mode="bindings" /> },
      { path: ROUTES.integrationRouting, element: <IntegrationControlPage mode="routing" /> },
      { path: ROUTES.integrationEntitlements, element: <IntegrationControlPage mode="rights" /> },
      { path: ROUTES.featureReadiness, element: <FeatureReadinessPage /> },
      { path: ROUTES.integrationHealth, element: <IntegrationControlPage mode="health" /> },
      { path: ROUTE_PATTERNS.integrationProduct, element: <IntegrationProductPage /> },
      { path: ROUTES.ai, element: <AiPage /> },
      { path: ROUTE_PATTERNS.tingeeLab, element: <TingeeLabPage /> },
      { path: ROUTE_PATTERNS.bankHubLab, element: <BankHubLabPage /> },
      { path: ROUTE_PATTERNS.einvoiceLab, element: <EinvoiceLabPage /> },
      { path: ROUTES.lab, element: <LabIndexPage /> },
      { path: ROUTES.labFixtures, element: <LabFixturesPage /> },
      { path: ROUTES.labRuns, element: <LabRunsPage /> },
      { path: ROUTE_PATTERNS.einvoiceAgency, element: <EinvoiceAgencyPage /> },
      { path: ROUTE_PATTERNS.credentialSetHistory, element: <CredentialSetHistoryPage /> },

      // ── Số liệu: mọi app, chọn từng app bằng `?app=` ──
      { path: ROUTES.audience, element: <AudiencePage /> },
      { path: ROUTES.subscriptions, element: <SubscriptionsPage /> },
      { path: ROUTES.revenue, element: <RevenuePage /> },
      { path: ROUTES.planMapping, element: <PlanMappingPage /> },
      { path: ROUTES.feedback, element: <FeedbackPage /> },
      { path: ROUTE_PATTERNS.feedbackDetail, element: <FeedbackDetailPage /> },
      { path: ROUTES.support, element: <SupportInboxPage /> },
      { path: ROUTE_PATTERNS.supportTicket, element: <SupportTicketPage /> },

      // ── Ứng dụng: màn riêng từng app. Route cụ thể khai TRƯỚC `:slug` cho dễ đọc (router v6 tự
      //    xếp theo độ cụ thể, nhưng người đọc thì không). ──
      { path: ROUTES.shopBankTransfer, element: <BankTransferPage /> },
      { path: ROUTES.shopIntegrationCards, element: <ShopIntegrationCardsPage /> },
      { path: ROUTES.bankTransferSharedHub, element: <SharedHubPage /> },
      { path: ROUTES.bankTransferPartnerReport, element: <PartnerReportPage /> },
      { path: ROUTES.bankTransferAppLinks, element: <AppLinksPage /> },
      { path: ROUTE_PATTERNS.app, element: <AppHomePage /> },
      { path: ROUTE_PATTERNS.appIntegrations, element: <IntegrationsPage /> },
      { path: ROUTE_PATTERNS.appAi, element: <AiPage /> },
      { path: ROUTE_PATTERNS.appWorkspaces, element: <CustomerWorkspacesPage /> },
      { path: ROUTE_PATTERNS.appWorkspace, element: <CustomerWorkspacePage /> },
      { path: ROUTE_PATTERNS.appUsers, element: <CustomerUsersPage /> },
      { path: ROUTE_PATTERNS.appAcquisitionOps, element: <AcquisitionOpsPage /> },
      { path: ROUTE_PATTERNS.appResale, element: <ResalePage /> },
      { path: ROUTE_PATTERNS.appInvoiceQuota, element: <InvoiceQuotaPage /> },
      { path: ROUTE_PATTERNS.appInvoiceQuotaWorkspace, element: <InvoiceQuotaWorkspacePage /> },

      // ── Hệ thống ──
      { path: ROUTES.sources, element: <SourcesPage /> },
      { path: ROUTE_PATTERNS.source, element: <SourceDetailPage /> },
      { path: ROUTES.reliability, element: <ReliabilityPage /> },
      { path: ROUTES.cost, element: <CostPage /> },
      { path: ROUTES.mailStatus, element: <MailStatusPage /> },
      { path: ROUTE_PATTERNS.mailStatusApp, element: <MailAppPage /> },
      { path: ROUTES.mailProviders, element: <MailProvidersPage /> },
      { path: ROUTE_PATTERNS.mailProvider, element: <MailProviderPage /> },
      { path: ROUTE_PATTERNS.service, element: <ServiceDetailPage /> },
      { path: ROUTES.dependencies, element: <DependencyPage /> },
      { path: ROUTES.products, element: <ProductsPage /> },
      { path: ROUTE_PATTERNS.product, element: <ProductDetailPage /> },
      { path: ROUTES.ops, element: <PlatformPage /> },
      { path: ROUTE_PATTERNS.run, element: <RunDetailPage /> },

      // ── Quản trị ──
      { path: ROUTES.staff, element: <StaffPage /> },
      { path: ROUTES.teams, element: <TeamsPage /> },
      { path: ROUTES.ownership, element: <OwnershipPage /> },

      // Đường dẫn cũ — chuyển hướng, giữ tham số và query.
      ...LEGACY_REDIRECTS.map((r) => ({ path: r.from, element: <LegacyRedirect to={r.to} /> })),

      // Mọi đường dẫn ở đây đều nằm sau đăng nhập, nên « gõ sai » là khả năng duy nhất còn lại —
      // đưa về trang mở đầu thay vì một màn 404 trắng.
      { path: "*", element: <Navigate to={ROUTES.attention} replace /> },
    ],
  },
];

/**
 * HQ-2026-009 — một route gốc KHÔNG đường dẫn, chỉ để mang `errorElement`: mọi trang ném lỗi lúc vẽ (kể cả khung
 * `AppShell`) rơi về `RouteErrorPage` — báo Sentry + nút tải lại — thay vì màn trắng của react-router.
 */
export const router = createBrowserRouter([
  { errorElement: <RouteErrorPage />, children: ROUTE_TREE },
]);
